import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { contentApi } from '../../services/contentApi';
import { projectCatalogCache } from '../../services/projectCatalogCache';
import { ProjectListItem } from '../../types/content';
import Input from '../../components/UI/Input';
import { PageLoading } from '../../components/UI/LoadingOverlay';
import CatalogFilterLayout, { CatalogFilterSelect } from '../../components/content/CatalogFilterLayout';
import { COURSE_IMAGES } from '../../config/courseImages';
import HeroBackdrop from '../../components/UI/HeroBackdrop';
import { heroImageFade, heroShell } from '../../content/heroThemes';
import { focusPageTop } from '../../utils/focusPageTop';
import { formatApproxHours, projectAudienceLabel } from '../../utils/projectDisplay';
import TechStackPreview from '../../components/content/TechStackPreview';

const ProjectsPage: React.FC = () => {
  const [projects, setProjects] = useState<ProjectListItem[]>([]);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [difficulty, setDifficulty] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const handle = window.setTimeout(() => setSearch(searchInput.trim()), 350);
    return () => window.clearTimeout(handle);
  }, [searchInput]);

  useEffect(() => {
    let cancelled = false;
    if (!projectCatalogCache.getList() || search) setLoading(true);
    contentApi.listProjects({
      search: search || undefined,
      difficulty: difficulty || undefined,
      pageSize: 50,
    }).then((r) => {
      if (cancelled) return;
      setProjects(r.data.projects || []);
      setError(null);
    }).catch(() => {
      if (!cancelled) setError('Unable to load projects.');
    }).finally(() => {
      if (!cancelled) setLoading(false);
    });
    return () => { cancelled = true; };
  }, [search, difficulty]);

  useEffect(() => {
    focusPageTop();
  }, [search, difficulty]);

  return (
    <div>
      <section className={heroShell('projects')}>
        <HeroBackdrop tone="projects" />
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-[1.35rem] md:py-[1.575rem] lg:py-[1.8rem]">
          <div className="grid lg:grid-cols-2 gap-[1.125rem] lg:gap-[1.8rem] items-center">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-primary-200">Self-paced · Learn by building</p>
              <h1 className="mt-3 text-2xl sm:text-3xl lg:text-4xl font-bold leading-tight">
                Build and ship something you can proudly showcase
              </h1>
              <p className="mt-4 text-sm sm:text-base text-primary-100/90 leading-relaxed">
                These are self-paced projects. Work from real-world specifications, features, UX, and architecture at your own pace, then submit your Git repository for review. After you start a project, you can optionally opt for a project mentor.
              </p>
            </div>
            <div className="relative">
              <div className="relative overflow-hidden rounded-xl shadow-2xl ring-1 ring-white/10">
                <img
                  src={COURSE_IMAGES.projectsHero}
                  alt="Engineers collaborating on a software project"
                  className="w-full h-[12.6rem] sm:h-[15.3rem] lg:h-[18rem] object-cover object-center"
                />
                <div className={heroImageFade('projects')} />
              </div>
            </div>
          </div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <CatalogFilterLayout
          applied={[
            ...(search ? [{ key: 'search', label: `Search: ${search}`, onRemove: () => { setSearchInput(''); setSearch(''); } }] : []),
            ...(difficulty ? [{ key: 'difficulty', label: `Difficulty: ${difficulty}`, onRemove: () => setDifficulty('') }] : []),
          ]}
          onClearFilters={() => {
            setSearchInput('');
            setSearch('');
            setDifficulty('');
          }}
          results={(
            <>
              {loading && <PageLoading label="Loading projects…" />}
              {error && <p className="p-3 rounded-md bg-error-50 text-error-600 border border-error-200">{error}</p>}
              {!loading && !error && projects.length === 0 && <p className="text-gray-500">No published projects match these filters.</p>}
              {projects.length > 0 && (
                <div className="catalog-panel">
                  <table className="min-w-full text-sm text-left">
                    <thead className="bg-gray-100 text-sm font-bold text-gray-900">
                      <tr>
                        <th className="px-4 py-3">Title</th>
                        <th className="px-4 py-3">Subtitle</th>
                        <th className="px-4 py-3">Company</th>
                        <th className="px-4 py-3">Difficulty</th>
                        <th className="px-4 py-3">Hours</th>
                        <th className="px-4 py-3">Ideal for</th>
                        <th className="px-4 py-3">Tech stack</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {projects.map((project) => (
                        <tr key={project.id} className="hover:bg-primary-50/40">
                          <td className="px-4 py-3 font-medium text-gray-900">
                            <Link to={`/projects/${project.slug}`} className="text-primary-700 hover:underline">
                              {project.title}
                            </Link>
                          </td>
                          <td className="px-4 py-3 text-gray-600 max-w-xs">{project.subtitle || '—'}</td>
                          <td className="px-4 py-3 text-gray-700">{project.company}</td>
                          <td className="px-4 py-3 text-gray-700">{project.difficulty}</td>
                          <td className="px-4 py-3 text-gray-700 whitespace-nowrap">{formatApproxHours(project.estimatedHours) || '—'}</td>
                          <td className="px-4 py-3 text-gray-700">{projectAudienceLabel(project.teamType)}</td>
                          <td className="px-4 py-3">
                            <TechStackPreview stack={project.techStack} />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        >
          <Input
            id="project-search"
            label="Search"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="redis, .net"
          />
          <CatalogFilterSelect label="Difficulty" value={difficulty} onChange={setDifficulty}>
            <option value="">All</option>
            <option>Beginner</option>
            <option>Intermediate</option>
            <option>Advanced</option>
          </CatalogFilterSelect>
        </CatalogFilterLayout>
      </div>
    </div>
  );
};

export default ProjectsPage;
